import { useState, useCallback } from 'react';
import { Sparkles, RefreshCw, AlertTriangle, Zap, Scale, Shield, Check } from 'lucide-react';
import { AIProgressLoader } from '../common/AIProgressLoader';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { buildSystemPrompt } from '../../data/aiContext';
import { apiFetch } from '../../lib/api';
import { buildDirectionsPrompt, parseDirectionsResponse } from '../../lib/strategicDirections';
import type { StrategicDirection } from '../../lib/strategicDirections';
import type { PackageConfig, PnLResult } from '../../types';
import type { BuyerProxyResult } from '../../lib/buyerProxy';

interface Props {
 config: PackageConfig;
 pnl: PnLResult;
 buyerProxy: BuyerProxyResult;
 segmentDemand: { segmentName: string; segmentRate: number; weight: number }[];
 directions: StrategicDirection[];
 chosenIndex: number | null;
 onDirectionsGenerated: (dirs: StrategicDirection[]) => void;
 onChoose: (index: number) => void;
}

function RiskIcon({ level }: { level: string }) {
 if (level === 'Wild') return <Zap size={14} className="text-purple-500" />;
 if (level === 'Moderate') return <Scale size={14} className="text-chart-amber" />;
 return <Shield size={14} className="text-chart-teal" />;
}

function riskColor(level: string): string {
 if (level === 'Wild') return 'border-chart-purple/30 bg-chart-purple/5';
 if (level === 'Moderate') return 'border-chart-amber/30 bg-chart-amber/5';
 return 'border-chart-teal/30 bg-chart-teal/5';
}

function riskBadge(level: string): string {
 if (level === 'Wild') return 'bg-chart-purple/15 text-chart-purple';
 if (level === 'Moderate') return 'bg-chart-amber/15 text-chart-amber';
 return 'bg-chart-teal/15 text-chart-teal';
}

export function StrategicDirections({ config, pnl, buyerProxy, segmentDemand, directions, chosenIndex, onDirectionsGenerated, onChoose }: Props) {
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 const generate = useCallback(async () => {
 setLoading(true);
 setError(null);
 try {
 const state = {
 assumptions: useSimulatorStore.getState().assumptions,
 packages: useSimulatorStore.getState().packages,
 activeTab: 'marketing' as const,
 selectedPrice: useSimulatorStore.getState().selectedPrice,
 };
 const systemPrompt = buildSystemPrompt(state);
 const prompt = buildDirectionsPrompt(config, pnl, buyerProxy, segmentDemand);

 const res = await apiFetch('/api/insights', {
 method: 'POST',
 body: JSON.stringify({ prompt, system: systemPrompt, maxTokens: 4096 }),
 });
 if (!res.ok) throw new Error(`API error: ${res.status}`);
 const data = await res.json();
 const parsed = parseDirectionsResponse(data.content);
 if (parsed.length === 0) throw new Error('Could not parse directions from AI response');
 onDirectionsGenerated(parsed);
 } catch (e) {
 setError(e instanceof Error ? e.message : 'Failed to generate');
 } finally {
 setLoading(false);
 }
 }, [config, pnl, buyerProxy, segmentDemand, onDirectionsGenerated]);

 if (directions.length === 0 && !loading && !error) {
 return (
 <div className="card text-center py-10">
 <Sparkles size={28} className="text-chart-teal mx-auto mb-3" />
 <h3 className="text-sm font-semibold text-primary mb-1">Strategic Positioning Directions</h3>
 <p className="text-xs text-muted mb-4 max-w-md mx-auto">
 GHOST AI will generate 3 strategic positioning directions for {config.name} — ranging from bold to conservative — based on your conjoint data and buyer profile.
 </p>
 <button onClick={generate} className="px-5 py-2.5 text-sm font-medium rounded-lg bg-chart-teal text-white hover:bg-teal-600 transition-colors inline-flex items-center gap-2">
 <Sparkles size={14} />
 Generate Directions
 </button>
 </div>
 );
 }

 return (
 <div>
 <div className="flex items-center justify-between mb-3">
 <div className="text-xs font-semibold uppercase tracking-wider text-muted">Step 2: Choose a Strategic Direction</div>
 <button onClick={generate} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-secondary hover:text-primary hover:bg-surface transition-colors disabled:opacity-50">
 <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
 {loading ? 'Generating...' : 'Regenerate'}
 </button>
 </div>

 {loading && (
 <div className="card flex items-center justify-center py-14">
 <AIProgressLoader
 estimatedSeconds={20}
 messages={[
 'Reading the conjoint tea leaves\u2026',
 'Studying what 1,001 people actually chose\u2026',
 'Debating bold vs. safe with the algorithm\u2026',
 'Channeling your buyer\u2019s inner monologue\u2026',
 'Testing positioning angles in the AI focus group\u2026',
 'Balancing ambition with cold hard data\u2026',
 'Crafting three very different paths to market\u2026',
 'Adding just the right amount of audacity\u2026',
 'Giving each direction a personality\u2026',
 'Your strategic directions are almost ready\u2026',
 ]}
 />
 </div>
 )}

 {error && (
 <div className="flex items-center gap-2 p-4 rounded-lg bg-metric-negative/10 text-metric-negative text-xs mb-4">
 <AlertTriangle size={14} />
 {error}
 <button onClick={generate} className="ml-auto underline">Retry</button>
 </div>
 )}

 {directions.length > 0 && !loading && (
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {directions.map((dir, i) => {
 const isChosen = chosenIndex === i;
 return (
 <div key={i} className={`rounded-xl border-2 p-5 transition-all ${isChosen ? 'border-chart-teal bg-chart-teal/5 shadow-md ring-1 ring-chart-teal/20' : riskColor(dir.riskLevel)}`}>
 <div className="flex items-center justify-between mb-3">
 <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${riskBadge(dir.riskLevel)}`}>
 <RiskIcon level={dir.riskLevel} />
 {dir.riskLevel}
 </span>
 {isChosen && (
 <span className="flex items-center gap-1 text-micro font-semibold text-chart-teal">
 <Check size={12} /> Chosen
 </span>
 )}
 </div>

 <h4 className="text-base font-bold text-primary mb-2">{dir.name}</h4>
 <p className="text-sm text-secondary leading-relaxed mb-3">{dir.positioningStatement}</p>

 <div className="space-y-2 mb-4">
 <div>
 <span className="text-xs font-semibold text-muted uppercase tracking-wider">Tone</span>
 <p className="text-sm text-secondary">{dir.tone}</p>
 </div>
 <div>
 <span className="text-xs font-semibold text-muted uppercase tracking-wider">Lead Pillar</span>
 <p className="text-sm text-secondary">{dir.leadPillar}</p>
 </div>
 <div>
 <span className="text-xs font-semibold text-muted uppercase tracking-wider">Emotional Trigger</span>
 <p className="text-sm text-secondary">{dir.emotionalTrigger}</p>
 </div>
 <div>
 <span className="text-xs font-semibold text-muted uppercase tracking-wider">Target Audience</span>
 <p className="text-sm text-secondary">{dir.targetAudience || "See rationale"}</p> </div>
 </div>

 <p className="text-xs text-muted italic mb-4 leading-relaxed">{dir.rationale}</p>

 <button
 onClick={() => onChoose(i)}
 disabled={isChosen}
 className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors ${
 isChosen
 ? 'bg-chart-teal text-white cursor-default'
 : 'bg-surface border border-border text-secondary hover:border-chart-teal hover:text-chart-teal'
 }`}
 >
 {isChosen ? 'Direction Chosen' : 'Choose This Direction'}
 </button>
 </div>
 );
 })}
 </div>
 )}

 <p className="text-micro text-muted italic mt-3">
 Directions generated by GHOST AI from conjoint data, buyer proxy demographics, and category context. Each is a strategic starting point — review and refine before deployment.
 </p>
 </div>
 );
}
